/**
 * BlumeAdmin UI State
 *
 * This state manages UI-related settings for the Blume Admin section of the application.
 * It is locally scoped to the '/blume-admin' route and only provided when that route is accessed.
 *
 * Features:
 * - Side menu expansion state management
 * - LocalStorage persistence for user preferences
 * - Automatic initialization on blume-admin route access
 */

import { Injectable } from '@angular/core';
import {
  State,
  Action,
  StateContext,
  Selector,
  NgxsOnInit,
  Store,
} from '@ngxs/store';
import {
  InitializeBlumeAdminUIStateAction,
  SetBlumeAdminSideMenuExpandedAction,
  ToggleBlumeAdminSideMenuExpandedAction,
} from './blume-admin-ui.actions';
import { AuthState } from 'src/app/store/auth/auth.state';
import { distinctUntilChanged } from 'rxjs/operators';

/**
 * BlumeAdmin UI State Model Interface
 *
 * Defines the shape of the BlumeAdmin UI state.
 */
export interface BlumeAdminUIStateModel {
  /**
   * Whether the side menu is currently expanded
   */
  sideMenuExpanded: boolean;
}

/**
 * Default state values
 */
const DEFAULT_BLUME_ADMIN_UI_STATE: BlumeAdminUIStateModel = {
  sideMenuExpanded: true,
};

/**
 * LocalStorage key for persisting BlumeAdmin UI state
 */
const BLUME_ADMIN_UI_STATE_STORAGE_KEY = 'blume_blume_admin_ui_state';

/**
 * BlumeAdmin UI State Class
 *
 * Manages UI-related state for the Blume Admin section, including side menu expansion.
 * This state automatically persists to localStorage.
 *
 * @example
 * // Select side menu expanded state
 * this.store.select(BlumeAdminUIState.sideMenuExpanded).subscribe(expanded => {
 *   console.log('Side menu expanded:', expanded);
 * });
 *
 * @example
 * // Dispatch actions
 * this.store.dispatch(new SetBlumeAdminSideMenuExpandedAction(true));
 * this.store.dispatch(new ToggleBlumeAdminSideMenuExpandedAction());
 */
@State<BlumeAdminUIStateModel>({
  name: 'blumeAdminUI',
  defaults: DEFAULT_BLUME_ADMIN_UI_STATE,
})
@Injectable()
export class BlumeAdminUIState implements NgxsOnInit {
  constructor(private store: Store) {}

  /**
   * Initialize state listeners
   *
   * Listens for auth state changes and resets state if user is not authenticated.
   * This handles all cases where user becomes unauthenticated (sign out, account deletion, etc.)
   */
  ngxsOnInit(ctx: StateContext<BlumeAdminUIStateModel>): void {
    let previousAuthState: boolean | null = null;

    // Subscribe to auth state changes
    this.store
      .select(AuthState.isAuthenticated)
      .pipe(distinctUntilChanged()) // Only emit when authentication status actually changes
      .subscribe((isAuthenticated) => {
        // Only clear if we're transitioning from authenticated to unauthenticated
        // (not on initial load where auth state might not be hydrated yet)
        if (previousAuthState === true && !isAuthenticated) {
          // Reset state
          ctx.setState(DEFAULT_BLUME_ADMIN_UI_STATE);
          console.log(
            '[BlumeAdminUIState] State cleared - user signed out'
          );
        }

        // Track previous auth state
        previousAuthState = isAuthenticated;
      });
  }

  // ========================================
  // Selectors
  // ========================================

  /**
   * Select the entire BlumeAdmin UI state
   *
   * @param state - The BlumeAdmin UI state
   * @returns The complete BlumeAdmin UI state model
   *
   * @example
   * this.store.select(BlumeAdminUIState.getState).subscribe(blumeAdminUIState => {
   *   console.log('BlumeAdmin UI State:', blumeAdminUIState);
   * });
   */
  @Selector()
  static getState(state: BlumeAdminUIStateModel): BlumeAdminUIStateModel {
    return state;
  }

  /**
   * Select the side menu expanded state
   *
   * @param state - The BlumeAdmin UI state
   * @returns Whether the side menu is expanded
   *
   * @example
   * this.store.select(BlumeAdminUIState.sideMenuExpanded).subscribe(expanded => {
   *   console.log('Side menu expanded:', expanded);
   * });
   */
  @Selector()
  static sideMenuExpanded(state: BlumeAdminUIStateModel): boolean {
    return state.sideMenuExpanded;
  }


  // ========================================
  // Action Handlers
  // ========================================

  /**
   * Initialize BlumeAdmin UI State Action Handler
   *
   * Loads the BlumeAdmin UI state from localStorage.
   * If no saved state exists, uses the default state.
   *
   * @param ctx - The state context
   */
  @Action(InitializeBlumeAdminUIStateAction)
  initializeBlumeAdminUIState(ctx: StateContext<BlumeAdminUIStateModel>): void {
    const savedState = this.loadStateFromStorage();

    if (savedState) {
      // Load saved state
      ctx.patchState(savedState);
    } else {
      // Save default state if none exists
      this.saveStateToStorage(ctx.getState());
    }
  }

  /**
   * Set Side Menu Expanded Action Handler
   *
   * Sets the side menu expansion state to a specific value and persists to localStorage.
   *
   * @param ctx - The state context
   * @param action - The action containing the expanded state
   */
  @Action(SetBlumeAdminSideMenuExpandedAction)
  setBlumeAdminSideMenuExpanded(
    ctx: StateContext<BlumeAdminUIStateModel>,
    action: SetBlumeAdminSideMenuExpandedAction
  ): void {
    ctx.patchState({ sideMenuExpanded: action.expanded });
    this.saveStateToStorage(ctx.getState());
  }

  /**
   * Toggle Side Menu Expanded Action Handler
   *
   * Toggles the current side menu expansion state and persists to localStorage.
   *
   * @param ctx - The state context
   */
  @Action(ToggleBlumeAdminSideMenuExpandedAction)
  toggleBlumeAdminSideMenuExpanded(
    ctx: StateContext<BlumeAdminUIStateModel>
  ): void {
    const currentState = ctx.getState();
    const newExpanded = !currentState.sideMenuExpanded;

    ctx.patchState({ sideMenuExpanded: newExpanded });
    this.saveStateToStorage(ctx.getState());
  }


  // ========================================
  // Private Helper Methods
  // ========================================

  /**
   * Save the current state to localStorage
   *
   * @param state - The state to save
   */
  private saveStateToStorage(state: BlumeAdminUIStateModel): void {
    try {
      localStorage.setItem(BLUME_ADMIN_UI_STATE_STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      console.error(
        'Failed to save BlumeAdmin UI state to localStorage:',
        error
      );
    }
  }

  /**
   * Load the state from localStorage
   *
   * @returns The saved state, or null if none exists or parsing fails
   */
  private loadStateFromStorage(): BlumeAdminUIStateModel | null {
    try {
      const savedState = localStorage.getItem(BLUME_ADMIN_UI_STATE_STORAGE_KEY);

      if (savedState) {
        return JSON.parse(savedState);
      }
    } catch (error) {
      console.error(
        'Failed to load BlumeAdmin UI state from localStorage:',
        error
      );
    }

    return null;
  }
}

